import { Drawer, DrawerProps, IconButton } from '@material-ui/core';
import { ArrowBack, Close } from '@material-ui/icons';
import { FormikContextTypeEnhanced } from '@superdispatch/forms';
import { useEventHandler } from '@superdispatch/hooks';
import {
  DrawerActions,
  DrawerContent as MuiDrawerContent,
  DrawerTitle,
  useUID,
} from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { Form, FormikProvider, FormikValues, useFormikContext } from 'formik';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PermissionAction } from 'shared/modules/permission/core/PermissionAction';
import { PermissionIcon } from 'shared/modules/permission/core/PermissionIcon';
import { PermissionDTO } from 'shared/modules/permission/PermissionDTO';
import { useLocationChangeEffect } from 'shared/routing/LocationChangeEffect';
import { Prompt } from 'shared/routing/NavigationBlock';
import styled from 'styled-components';

const DrawerContent = styled(MuiDrawerContent)<{ padding?: string }>`
  padding: ${({ padding }) => padding};
`;

type FormPropsWithDataAttributes = React.FormHTMLAttributes<HTMLFormElement> &
  Record<`data-${string}`, string>;

interface FormikDrawerState {
  isSubmitting: boolean;
  dirty: boolean;
  isSubmitted: boolean;
}

interface FormikDrawerContext {
  setFormState?: (formState: FormikDrawerState) => void;
  formState?: FormikDrawerState;
  handleClose?: () => void;
  setCloseRequestHandler?: (handler: (() => void) | undefined) => void;
}

const context = createContext<FormikDrawerContext>({});

interface FormikDrawerProps {
  open?: boolean;
  onClose?: () => void;
  children?: ReactNode;
  drawerProps?: Omit<DrawerProps, 'open' | 'anchor' | 'onClose'>;
  enableConfirmationOnClose?: boolean;
}

export function FormikDrawer({
  onClose,
  children,
  drawerProps,
  open = false,
  enableConfirmationOnClose = false,
}: FormikDrawerProps) {
  const [formState, setFormState] = useState<FormikDrawerState>({
    isSubmitting: false,
    dirty: false,
    isSubmitted: false,
  });
  const [closeRequestHandler, setCloseRequestHandlerState] = useState<
    (() => void) | undefined
  >();
  const setCloseRequestHandler = useCallback(
    (handler: (() => void) | undefined) =>
      setCloseRequestHandlerState(() => handler),
    [],
  );
  const onCloseEvent = useEventHandler<void>(onClose);

  const handleClose = useCallback(() => {
    if (closeRequestHandler) {
      closeRequestHandler();
      return;
    }

    if (formState.isSubmitting) return;

    if (
      enableConfirmationOnClose &&
      formState.dirty &&
      !formState.isSubmitted
    ) {
      // eslint-disable-next-line no-alert
      const isConfirmed = window.confirm(
        'Changes have not been saved. Leaving will result in unsaved changes being lost.',
      );
      if (!isConfirmed) return;
    }
    onCloseEvent();
  }, [closeRequestHandler, enableConfirmationOnClose, onCloseEvent, formState]);

  const ctx = useMemo<FormikDrawerContext>(
    () => ({ setFormState, handleClose, formState, setCloseRequestHandler }),
    [setFormState, handleClose, formState, setCloseRequestHandler],
  );

  useEffect(() => {
    if (!open) {
      setFormState({ isSubmitting: false, dirty: false, isSubmitted: false });
    }
  }, [open]);

  return (
    <Drawer open={open} anchor="right" onClose={handleClose} {...drawerProps}>
      <context.Provider value={ctx}>{children}</context.Provider>
    </Drawer>
  );
}

export interface BaseFormikDrawerContentProps {
  title?: ReactNode;
  disableClose?: boolean;
  enableBack?: boolean;
  onBack?: () => void;
  actions?: ReactNode;
  children?: ReactNode;
  formik: FormikContextTypeEnhanced<FormikValues, unknown>;
  minWidth?: string;
  maxWidth?: string;
  disableNavigationBlock?: boolean;
  padding?: string;
  formProps?: FormPropsWithDataAttributes;
  onCloseRequest?: () => void;
}

type FormikDrawerContentProps = BaseFormikDrawerContentProps &
  (
    | { onClose?: () => void; disableAutoClose: true }
    | { onClose: () => void; disableAutoClose?: false }
  );

export function FormikDrawerContent({
  formik,
  title,
  disableClose,
  enableBack,
  actions,
  children,
  onBack,
  onClose,
  disableAutoClose,
  minWidth,
  maxWidth,
  disableNavigationBlock,
  padding,
  formProps,
  onCloseRequest,
}: FormikDrawerContentProps) {
  const uid = useUID();
  const iconID = `${uid}-icon`;
  const labelID = `${uid}-label`;
  const { setFormState, handleClose, setCloseRequestHandler } =
    useContext(context);

  useEffect(() => {
    setCloseRequestHandler?.(onCloseRequest);
    return () => setCloseRequestHandler?.(undefined);
  }, [onCloseRequest, setCloseRequestHandler]);

  const { dirty, status, isSubmitting } = formik;
  const isSubmitted = status.type === 'submitted';

  useEffect(() => {
    setFormState?.({ isSubmitting, dirty, isSubmitted });
  }, [isSubmitting, dirty, isSubmitted, setFormState]);

  useLocationChangeEffect(() => {
    if (!disableAutoClose) {
      onClose();
    }
  });

  return (
    <FormikProvider value={formik}>
      {!!title && (
        <DrawerTitle
          title={title}
          titleTypographyProps={{ id: labelID }}
          startAction={
            enableBack && (
              <IconButton onClick={onBack} edge="start" aria-label="back">
                <ArrowBack />
              </IconButton>
            )
          }
          endAction={
            disableClose ? null : (
              <IconButton
                edge="end"
                onClick={onCloseRequest ?? handleClose}
                disabled={isSubmitting}
                aria-labelledby={`${iconID} ${labelID}`}
              >
                <Close id={iconID} aria-label="close" />
              </IconButton>
            )
          }
        />
      )}

      <Form
        {...formProps}
        style={{ minWidth, maxWidth }}
        aria-labelledby={labelID}
      >
        <Prompt
          when={
            isSubmitting || (dirty && !isSubmitted && !disableNavigationBlock)
          }
          message="Changes have not been saved. Leaving will result in unsaved changes being lost."
        />
        <DrawerContent padding={padding}>{children}</DrawerContent>
        {actions}
      </Form>
    </FormikProvider>
  );
}

interface FormikDrawerActionsProps<T extends keyof PermissionDTO> {
  submitButtonLabel?: ReactNode;
  submitDisabled?: boolean;
  name?: T;
  permission?: keyof NonNullable<PermissionDTO[T]>;
  fullWidth?: boolean;
}

export function FormikDrawerActions<T extends keyof PermissionDTO>({
  submitButtonLabel = 'Save',
  submitDisabled = false,
  name,
  permission,
  fullWidth,
}: FormikDrawerActionsProps<T>) {
  const { isSubmitting } = useFormikContext();

  if (name && permission) {
    return (
      <DrawerActions>
        <PermissionAction name={name} permission={permission}>
          <Button
            startIcon={<PermissionIcon name={name} permission={permission} />}
            type="submit"
            pending={isSubmitting}
            disabled={submitDisabled}
            fullWidth={fullWidth}
          >
            {submitButtonLabel}
          </Button>
        </PermissionAction>
      </DrawerActions>
    );
  }

  return (
    <DrawerActions>
      <Button
        type="submit"
        pending={isSubmitting}
        disabled={submitDisabled}
        fullWidth={fullWidth}
      >
        {submitButtonLabel}
      </Button>
    </DrawerActions>
  );
}
